import React, { useState, useEffect, useRef } from 'react';
import LoadingAnimation from './LoadingAnimation';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Textarea } from './ui/textarea';
import { ArrowLeft, Send, Heart, BookOpen, Sparkles, Check } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeartOfAction = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const messagesEndRef = useRef(null);

  // Session state
  const [sessionId, setSessionId] = useState(null);
  const [cnvFamiliarity, setCnvFamiliarity] = useState(null);
  const [showFamiliaritySelector, setShowFamiliaritySelector] = useState(true);

  // Chat state
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // End session state
  const [isEnding, setIsEnding] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [summary, setSummary] = useState(null);
  const [needsIdentified, setNeedsIdentified] = useState([]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const familiarityLevels = [
    {
      id: 'beginner',
      label: 'Iniciante',
      description: 'Estou conhecendo a Inteligência Relacional agora',
      icon: '🌱'
    },
    {
      id: 'intermediate',
      label: 'Intermediário',
      description: 'Conheço os 4 componentes básicos',
      icon: '🌿'
    },
    {
      id: 'advanced',
      label: 'Avançado',
      description: 'Pratico Inteligência Relacional regularmente',
      icon: '🌳'
    }
  ];

  const startSession = async (level) => {
    setCnvFamiliarity(level);
    setShowFamiliaritySelector(false);
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API}/heart-of-action/start`,
        {
          cnv_familiarity: level,
          initial_situation: null
        },
        { withCredentials: true }
      );

      setSessionId(response.data.session_id);
      setMessages([{
        role: 'assistant',
        content: response.data.initial_message,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error starting session:', error);
      // Show error and allow retry
      setShowFamiliaritySelector(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping || !sessionId) return;

    const userMessage = {
      role: 'user',
      content: input.trim(),
      timestamp: new Date()
    };

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');
    setIsTyping(true);

    try {
      const response = await axios.post(
        `${API}/heart-of-action/chat`,
        {
          session_id: sessionId,
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
          user_message: input.trim()
        },
        { withCredentials: true }
      );

      setMessages([...updatedMessages, {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date()
      }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages([...updatedMessages, {
        role: 'assistant',
        content: 'Desculpe, houve um erro ao processar sua mensagem. Podemos tentar novamente?',
        timestamp: new Date()
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleEndSession = async () => {
    if (!sessionId || messages.length < 2) return;

    setIsEnding(true);

    try {
      const response = await axios.post(
        `${API}/heart-of-action/end`,
        {
          session_id: sessionId,
          messages: messages.map(m => ({ role: m.role, content: m.content }))
        },
        { withCredentials: true }
      );

      setSummary(response.data.summary);
      setNeedsIdentified(response.data.needs_identified || []);
      setShowSummary(true);
    } catch (error) {
      console.error('Error ending session:', error);
      alert('Erro ao gerar resumo. Tente novamente.');
    } finally {
      setIsEnding(false);
    }
  };

  const handleNewReflection = () => {
    setSessionId(null);
    setMessages([]);
    setSummary(null);
    setNeedsIdentified([]);
    setShowSummary(false);
    setShowFamiliaritySelector(true);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Initial loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-purple-50 to-blue-50">
        <div className="text-center">
          <LoadingAnimation size="md" className="mx-auto mb-4" />
          <p className="text-gray-600">Iniciando reflexão...</p>
        </div>
      </div>
    );
  }

  // Summary screen
  if (showSummary && summary) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-blue-50 p-4">
        <div className="max-w-3xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <Button
              onClick={() => navigate('/simulators')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>

          {/* Summary Card */}
          <Card className="p-8 bg-white border-2 border-purple-200">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                <Heart className="w-8 h-8 text-purple-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Reflexão Concluída
              </h2>
              <p className="text-gray-600">
                Aqui está o resumo da sua reflexão para guardar no diário
              </p>
            </div>

            {/* Needs Identified */}
            {needsIdentified.length > 0 && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Necessidades identificadas
                </h3>
                <div className="flex flex-wrap gap-2">
                  {needsIdentified.map((need, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-white rounded-full text-sm text-purple-700 border border-purple-200"
                    >
                      {need}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Summary Text */}
            <div className="p-6 bg-gradient-to-br from-gray-50 to-purple-50 rounded-lg border border-gray-200">
              <div className="flex items-start gap-3 mb-3">
                <BookOpen className="w-5 h-5 text-gray-500 mt-0.5" />
                <span className="text-sm font-medium text-gray-500">Entrada para o Diário</span>
              </div>
              <p className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                {summary}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mt-8 justify-center">
              <Button
                onClick={handleNewReflection}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <Heart className="w-4 h-4 mr-2" />
                Nova Reflexão
              </Button>
              <Button
                onClick={() => navigate('/diary')}
                variant="outline"
                className="border-purple-300 text-purple-700"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Ir para o Diário
              </Button>
              <Button
                onClick={() => navigate('/simulators')}
                variant="ghost"
              >
                Voltar aos Simuladores
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Familiarity selector screen
  if (showFamiliaritySelector) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-blue-50 p-4">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <Button
              onClick={() => navigate('/simulators')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Voltar
            </Button>
          </div>

          {/* Welcome Card */}
          <Card className="p-8 bg-white border-2 border-purple-200 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 mb-6">
              <Heart className="w-10 h-10 text-purple-600" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-3">
              O Coração da Ação
            </h1>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Vamos explorar juntos o que pulsa por trás das suas ações.
              Escolha seu nível de familiaridade com a Comunicação Não-Violenta.
            </p>

            <div className="grid gap-4 max-w-lg mx-auto">
              {familiarityLevels.map((level) => (
                <button
                  key={level.id}
                  onClick={() => startSession(level.id)}
                  className="p-4 rounded-xl border-2 border-gray-200 hover:border-purple-400 hover:bg-purple-50 transition-all duration-200 text-left flex items-center gap-4 group"
                >
                  <div className="text-3xl group-hover:scale-110 transition-transform">
                    {level.icon}
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-gray-900">{level.label}</div>
                    <div className="text-sm text-gray-600">{level.description}</div>
                  </div>
                  <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                    <ArrowLeft className="w-5 h-5 text-purple-600 rotate-180" />
                  </div>
                </button>
              ))}
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Main chat interface
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-blue-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-purple-100 p-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate('/simulators')}
              variant="ghost"
              size="sm"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center">
                <Heart className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h1 className="font-semibold text-gray-900">O Coração da Ação</h1>
                <p className="text-xs text-gray-500">Reflexão guiada em Inteligência Relacional</p>
              </div>
            </div>
          </div>

          <Button
            onClick={handleEndSession}
            disabled={isEnding || messages.length < 2}
            variant="outline"
            size="sm"
            className="border-purple-300 text-purple-700 hover:bg-purple-50"
          >
            {isEnding ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-600 mr-2"></div>
                Gerando resumo...
              </>
            ) : (
              <>
                <Check className="w-4 h-4 mr-2" />
                Encerrar Reflexão
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-hidden">
        <ScrollArea className="h-full">
          <div className="max-w-3xl mx-auto p-4 space-y-4 pb-32">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[85%] p-4 rounded-2xl ${message.role === 'user'
                      ? 'bg-purple-600 text-white rounded-br-md'
                      : 'bg-white border border-purple-100 text-gray-800 rounded-bl-md'
                    }`}
                >
                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                </div>
              </div>
            ))}

            {/* Typing indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-purple-100 p-4 rounded-2xl rounded-bl-md">
                  <div className="flex space-x-2">
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-purple-100 p-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex gap-3">
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Compartilhe o que está em seu coração..."
              disabled={isTyping || isEnding}
              className="min-h-[60px] max-h-[150px] resize-none border-purple-200 focus:border-purple-400 focus:ring-purple-400"
              rows={2}
            />
            <Button
              onClick={handleSend}
              disabled={!input.trim() || isTyping || isEnding}
              className="bg-purple-600 hover:bg-purple-700 self-end h-[60px] w-[60px]"
            >
              <Send className="w-5 h-5" />
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            Uma reflexão guiada sobre necessidades humanas universais
          </p>
        </div>
      </div>
    </div>
  );
};

export default HeartOfAction;


